/**
 * Pure functions for service worker web push handling.
 * Unit testable without browser/ServiceWorker globals.
 */

export const FALLBACK_NOTIFICATION_URL = '/notifications';
export const DEFAULT_NOTIFICATION_TITLE = 'Trade Connector';
export const DEFAULT_NOTIFICATION_BODY = 'You have received a new notification.';
export const DEFAULT_NOTIFICATION_ICON = '/android-chrome-192x192.png';

export interface IncomingWebPushPayload {
  title?: string;
  body?: string;
  url?: string;
  tag?: string;
  messageId?: string;
}

export interface WebPushNotificationOptions {
  body: string;
  icon: string;
  badge: string;
  tag?: string;
  /** With a tag, alert again when a newer push replaces the thread's notification. */
  renotify?: boolean;
  data: {
    url: string;
    messageId?: string;
  };
}

export interface PreparedNotification {
  title: string;
  options: WebPushNotificationOptions;
}

export interface MinimalWindowClient {
  focused: boolean;
  visibilityState?: string;
}

const PATH_CHECK_ORIGIN = 'https://path-check.invalid';

/**
 * Ensures a push navigation URL is a valid, same-origin site path.
 * Rejects non-strings, protocol-relative ('//evil.com'), backslash tricks ('/\\evil.com'),
 * and empty values, falling back to '/notifications'.
 */
export function toSafePushUrl(url: unknown): string {
  if (typeof url !== 'string') {
    return FALLBACK_NOTIFICATION_URL;
  }

  const trimmed = url.trim();
  if (!trimmed.startsWith('/')) {
    return FALLBACK_NOTIFICATION_URL;
  }

  // Reject protocol-relative '//' or Windows/WebKit path confusion '/\'
  if (trimmed.startsWith('//') || trimmed.startsWith('/\\')) {
    return FALLBACK_NOTIFICATION_URL;
  }

  // The URL parser drops tabs and newlines, so '/\t/evil.com' is really '//evil.com':
  // resolve it the way the browser will and keep it only if it stays on our origin.
  let parsed: URL;
  try {
    parsed = new URL(trimmed, PATH_CHECK_ORIGIN);
  } catch {
    return FALLBACK_NOTIFICATION_URL;
  }
  if (parsed.origin !== PATH_CHECK_ORIGIN) {
    return FALLBACK_NOTIFICATION_URL;
  }

  return `${parsed.pathname.replace(/^\/+/, '/')}${parsed.search}${parsed.hash}`;
}

/**
 * Safely parses the raw payload received by the push event.
 * Tolerates malformed JSON, strings, or missing data.
 */
export function parseWebPushPayload(raw: unknown): IncomingWebPushPayload | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (typeof data !== 'object' || data === null) {
    return null;
  }

  const record = data as Record<string, unknown>;
  const payload: IncomingWebPushPayload = {};

  if (typeof record.title === 'string') {
    payload.title = record.title;
  }
  if (typeof record.body === 'string') {
    payload.body = record.body;
  }
  if (typeof record.url === 'string') {
    payload.url = record.url;
  }
  if (typeof record.tag === 'string') {
    payload.tag = record.tag;
  }
  if (typeof record.messageId === 'string') {
    payload.messageId = record.messageId;
  }

  return payload;
}

/**
 * Builds title and NotificationOptions from a raw push payload.
 * Provides safe defaults for malformed or missing data.
 */
export function buildNotificationData(rawPayload: unknown): PreparedNotification {
  const parsed = parseWebPushPayload(rawPayload);

  if (!parsed) {
    return {
      title: DEFAULT_NOTIFICATION_TITLE,
      options: {
        body: DEFAULT_NOTIFICATION_BODY,
        icon: DEFAULT_NOTIFICATION_ICON,
        badge: DEFAULT_NOTIFICATION_ICON,
        data: {
          url: FALLBACK_NOTIFICATION_URL,
        },
      },
    };
  }

  const title = parsed.title?.trim() || DEFAULT_NOTIFICATION_TITLE;
  const body = parsed.body ?? '';
  const url = toSafePushUrl(parsed.url);

  const options: WebPushNotificationOptions = {
    body,
    icon: DEFAULT_NOTIFICATION_ICON,
    badge: DEFAULT_NOTIFICATION_ICON,
    data: {
      url,
      ...(parsed.messageId ? { messageId: parsed.messageId } : {}),
    },
  };

  if (parsed.tag && parsed.tag.trim().length > 0) {
    options.tag = parsed.tag.trim();
    options.renotify = true;
  }

  return { title, options };
}

/**
 * True where the browser lets a push end without a visible notification: Chromium (a site with a
 * visible tab is exempt) and desktop Firefox. Safari/WebKit counts every such push and drops the
 * subscription after three, so there (and on iOS, where every browser is WebKit) we always show it.
 */
export function engineAllowsSilentPush(userAgent: string): boolean {
  if (/iPhone|iPad|iPod/.test(userAgent)) return false;
  return /Chrome\/|Chromium\/|Firefox\//.test(userAgent);
}

/**
 * Suppresses OS push notifications when a focused, visible window client of our origin exists
 * and the browser allows it (see engineAllowsSilentPush). The site's in-app UI (bell counter and
 * audio) already informs active users.
 */
export function shouldSuppressPushNotification(
  clients: readonly MinimalWindowClient[],
  userAgent: string,
): boolean {
  if (!engineAllowsSilentPush(userAgent)) return false;
  return clients.some((client) => client.focused && client.visibilityState !== 'hidden');
}

/**
 * Selects an existing window client to focus upon notification click.
 * Prefers a focused client, then a visible one, then the first available client.
 */
export function findClientToFocus<T extends MinimalWindowClient>(clients: readonly T[]): T | undefined {
  if (clients.length === 0) {
    return undefined;
  }
  return (
    clients.find((client) => client.focused) ??
    clients.find((client) => client.visibilityState === 'visible') ??
    clients[0]
  );
}

/**
 * Message the worker posts to open windows when the browser replaces this
 * site's push subscription, so a signed-in tab can register the new one.
 */
export interface SubscriptionChangeMessage {
  type: 'pushsubscriptionchange';
  oldEndpoint: string | null;
}

export function buildSubscriptionChangeMessage(oldEndpoint: unknown): SubscriptionChangeMessage {
  return {
    type: 'pushsubscriptionchange',
    oldEndpoint: typeof oldEndpoint === 'string' && oldEndpoint.length > 0 ? oldEndpoint : null,
  };
}
